import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import { getToken } from '../../utils/auth';
import { FileText, User, ArrowLeft, Search, GraduationCap, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function UnevaluatedApplications() {
  const navigate = useNavigate();
  const { evaluatorId, programId } = useParams();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUnevaluatedApplications();
  }, [evaluatorId, programId]);

  useEffect(() => {
    filterApplications();
  }, [searchTerm, applications]);

  const fetchUnevaluatedApplications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-scholarship-application/unevaluated?programId=${programId}&evaluatorId=${evaluatorId}`,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
          },
        }
      );
      setApplications(response.data || []);
      setFilteredApplications(response.data || []);
    } catch (err) {
      setError("Failed to fetch unevaluated applications");
      toast.error("Failed to load applications", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    if (searchTerm) {
      filtered = filtered.filter(
        (app) =>
          app.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.user?.nationalId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredApplications(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleDownloadExcel = () => {
    const excelData = filteredApplications.map((application, index) => ({
      "#": index + 1,
      "First Name": application.user?.firstName || "",
      "Last Name": application.user?.lastName || "",
      "Email": application.user?.email || "",
      "National ID": application.user?.nationalId || "",
      "Phone Number": application.user?.phoneNumber || "",
      "Gender": application.user?.gender || "",
      "Apply Level": application.applyLevel || "",
      "Priority Course": application.priorityCourse?.courseName || "",
      "Course Sector": application.priorityCourse?.courseSector || "",
      "Priority Level": application.priorityCourse?.priorityLevel || "",
      "Academic Merit": application.academicMerit?.letterGrade || "",
      "Current Level": application.currentLevel || "",
      "Working Place": application.workingPlace || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unevaluated Applications");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      { wch: 20 },
      { wch: 15 },
      { wch: 10 },
      { wch: 20 },
      { wch: 30 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Unevaluated_Applications_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Applications exported successfully!", {
      position: "top-center",
      autoClose: 3000,
      theme: "colored",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <FileText className="w-8 h-8 text-hec-600" />
              </div>
              <p className="text-hec-600 text-lg">Loading applications...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBarComponent />
      <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
        <SidebarComponent />
      </aside>

      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => navigate("/evaluator-report")}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="w-14 h-14 bg-gradient-to-r from-hec-600 to-hec-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-hec-700">Unevaluated Applications</h1>
                      <p className="text-hec-600 mt-1">Applications pending evaluation</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDownloadExcel}
                    disabled={filteredApplications.length === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Excel</span>
                  </button>
                </div>

                {/* Summary Card */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-600 font-medium">Total Unevaluated Applications</p>
                      <p className="text-3xl font-bold text-orange-700 mt-1">{filteredApplications.length}</p>
                    </div>
                    <FileText className="w-12 h-12 text-orange-500 opacity-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hec-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or national ID..."
                  className="w-full pl-10 pr-4 py-3 border border-hec-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-transparent transition-all duration-200 text-hec-900"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Applications Table */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
              <h2 className="text-xl font-semibold text-hec-700 mb-4">Application List</h2>

              {filteredApplications.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No unevaluated applications found.</p>
                  <p className="text-gray-500 text-sm mt-2">All applications have been evaluated.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-hec-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">#</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Full Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">National ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Gender</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Apply Level</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Priority Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">Academic Merit</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-hec-700 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredApplications.map((application, index) => (
                        <tr key={application.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-hec-100 rounded-full flex items-center justify-center mr-3">
                                <User className="w-5 h-5 text-hec-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {application.user?.firstName} {application.user?.lastName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {application.user?.email || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {application.user?.nationalId || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {application.user?.gender || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {application.applyLevel || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {application.priorityCourse?.courseName || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {application.academicMerit?.letterGrade || "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => navigate(`/assessment/${application.id}/${programId}`)}
                              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 shadow-sm"
                            >
                              <GraduationCap className="w-4 h-4 mr-2" />
                              Assess Now
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
